import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import logo from '@/assets/aksaaf-logo.jpg';

function randomToken(len = 24) {
    const bytes = crypto.getRandomValues(new Uint8Array(len));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').slice(0, len);
}

export default function OnboardingPage() {
    const { user, refreshProfile, signOut } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [license, setLicense] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data: org, error: orgErr } = await supabase
                .from('organizations')
                .insert({
                    name: name.trim(),
                    license_number: license.trim() || null,
                    phone: phone.trim() || null,
                    email: user.email,
                })
                .select()
                .single();
            if (orgErr) throw new Error(orgErr.message);

            const { error: profileErr } = await supabase
                .from('profiles')
                .upsert({
                    id: user.id,
                    email: user.email,
                    full_name: user.user_metadata?.full_name || user.email,
                    organization_id: org.id,
                    role: 'admin',
                });
            if (profileErr) throw new Error(profileErr.message);

            await supabase.from('lead_sources_config').insert([
                { organization_id: org.id, source_type: 'website', website_api_key: 'aksaaf_' + randomToken(24), is_enabled: true },
                { organization_id: org.id, source_type: 'meta', meta_webhook_verify_token: randomToken(20), is_enabled: false },
                { organization_id: org.id, source_type: 'bayut', is_enabled: true },
                { organization_id: org.id, source_type: 'propertyfinder', is_enabled: true },
                { organization_id: org.id, source_type: 'whatsapp', is_enabled: true },
                { organization_id: org.id, source_type: 'walkin', is_enabled: true },
                { organization_id: org.id, source_type: 'referral', is_enabled: true },
            ]);

            await refreshProfile();
            toast.success('Workspace ready!');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.message || 'Setup failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center p-6">
            <div className="w-full max-w-lg bg-white border border-gray-200 rounded-xl shadow-sm p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-md border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img src={logo} alt="AKSAAF" className="h-9 w-9 object-contain" />
                    </div>
                    <div>
                        <div className="font-bold tracking-tight text-aks-text leading-none">AKSAAF Properties</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">CRM · Welcome · Step 1</div>
                    </div>
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-aks-text">Set up your brokerage</h1>
                <p className="text-[13px] text-gray-500 mt-1">A few details to get your workspace ready. You can change these later in Settings.</p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="onboarding-form">
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">Brokerage / company name *</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 focus:border-aks-gold outline-none text-[14px] aks-focus-ring" data-testid="input-org-name" placeholder="e.g. AKSAAF Properties" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">RERA / DED license number</label>
                        <input value={license} onChange={(e) => setLicense(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 focus:border-aks-gold outline-none text-[14px] aks-focus-ring" placeholder="Optional" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">Office phone</label>
                        <input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 focus:border-aks-gold outline-none text-[14px] aks-focus-ring" placeholder="+971 ..." />
                    </div>
                    <button type="submit" disabled={loading || !name} className="mt-2 w-full h-11 rounded-md bg-aks-gold text-aks-navy font-semibold text-[14px] hover:brightness-105 disabled:opacity-60 aks-focus-ring" data-testid="submit-onboarding">
                        {loading ? 'Setting up…' : 'Create workspace →'}
                    </button>
                    <button type="button" onClick={signOut} className="w-full text-[12px] text-gray-500 hover:text-aks-danger">Sign out and start over</button>
                </form>
            </div>
        </div>
    );
}
